import type { FC, ReactNode } from 'react';
import { Link } from 'react-router';

import { copyrightStyle } from '@/features/storefront/appearance';
import { StorefrontContainer } from '@/features/storefront/components/StorefrontContainer';
import { StorefrontImage } from '@/features/storefront/components/StorefrontImage';
import { useShopContent } from '@/features/storefront/content/useContent';
import { useCategoryPath, useShelfCategories } from '@/features/storefront/hooks/useShelfCategories';
import { LanguageSwitcherSlot } from '@/features/storefront/i18n/LanguageSwitcher';
import {
  BlogLinks,
  MenuLink,
  PageLinks,
} from '@/features/storefront/layouts/chrome/ChromeParts';
import {
  FOOTER_CATEGORY_LIMIT,
  categoryColumns,
  menuItems,
  type FooterProps,
} from '@/features/storefront/layouts/chrome/types';
import { AppStoreBadges } from '@/features/storefront/layouts/chrome/FooterExtras';
import { InstallAppSlot } from '@/features/storefront/pwa/InstallAppSlot';
import { cn } from '@/lib/cn';
import { heroRitual } from '@/previewThemes/lumera/photos';

/**
 * Lumera's footer grid, by how many link columns stand beside the wordmark.
 * Written out because Tailwind reads class names out of the source and never
 * sees a string built at runtime.
 */
const LUMERA_FOOTER_GRID: Record<number, string> = {
  0: '',
  1: 'lg:grid-cols-[2fr_1fr]',
  2: 'lg:grid-cols-[2fr_1fr_1fr]',
  3: 'lg:grid-cols-[2fr_1fr_1fr_1fr]',
  4: 'lg:grid-cols-[2fr_1fr_1fr_1fr_1fr]',
};

/**
 * The espresso the footer is set on.
 *
 * Fixed rather than taken from the merchant's palette: the footer is the one
 * dark band on an otherwise light page, and its copy is set in cream over it.
 * A merchant who repaints their text colour to something light would otherwise
 * get light-on-light at the bottom of every page.
 */
const ESPRESSO = '#2a201b';
const CREAM = '#f5ebe2';

/** One footer column: a serif heading over its links. */
function LumeraColumn({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div>
      <p
        className="mb-4 text-2xl"
        style={{ fontFamily: 'var(--storefront-heading-font)', color: CREAM }}
        aria-hidden={title ? undefined : true}
      >
        {title ?? ' '}
      </p>
      {children}
    </div>
  );
}

/**
 * The dark close to a light page.
 *
 * An espresso band with a still-life photographed faintly through it, the
 * wordmark and what the shop is on the left, then the merchant's footer menu,
 * the shop's shelves and its pages. Same column arithmetic as the other
 * families - each column skipped when it would be empty, the grid sized to
 * what is left - so a new shop never shows a heading with nothing under it.
 */
export const Footer: FC<FooterProps> = ({ storeName, footer, description, menu, preview }) => {
  const items = menuItems(menu);
  const shelfColumns = categoryColumns(
    useShelfCategories({ limit: FOOTER_CATEGORY_LIMIT, enabled: !preview }),
  );
  const categoryPath = useCategoryPath();
  const copy = useShopContent();
  const columns = (items.length > 0 ? 1 : 0) + shelfColumns.length + (preview ? 0 : 1);
  const shopTitle = copy('theme.footer_shop');
  const shelvesTitle =
    menu?.name.trim().toLowerCase() === shopTitle.trim().toLowerCase()
      ? copy('header.menu_categories')
      : shopTitle;

  return (
    <footer
      className="relative isolate overflow-hidden pt-16 text-[14px]"
      style={{ backgroundColor: ESPRESSO, color: `${CREAM}cc` }}
    >
      {/* Decoration only, so it carries no alt text and sits behind a scrim
          heavy enough that the copy over it never depends on the picture. */}
      <StorefrontImage
        url={heroRitual}
        alt=""
        aria-hidden
        // It holds its own faint opacity, which a fade-in would override.
        fade={false}
        placeholder={false}
        className="absolute inset-0 -z-10 size-full object-cover opacity-[0.12]"
      />
      <StorefrontContainer
        className={cn('grid gap-10 pb-12 text-start sm:grid-cols-2', LUMERA_FOOTER_GRID[columns])}
      >
        <div>
          <p
            className="text-5xl lowercase leading-none"
            style={{ fontFamily: 'var(--storefront-heading-font)', color: CREAM }}
          >
            {storeName}
          </p>
          {description ? <p className="mt-5 max-w-[26rem] leading-relaxed">{description}</p> : null}
        </div>
        {items.length > 0 ? (
          <LumeraColumn title={menu?.name ?? shopTitle}>
            <nav aria-label={menu?.name} className="flex flex-col items-start gap-3">
              {items.map((item) => (
                <MenuLink
                  key={`${item.label}:${item.url}`}
                  item={item}
                  preview={preview}
                  className="hover:text-white"
                />
              ))}
            </nav>
          </LumeraColumn>
        ) : null}
        {shelfColumns.map((column, index) => (
          <LumeraColumn key={column[0].id} title={index === 0 ? shelvesTitle : undefined}>
            <nav
              aria-label={
                index === 0 ? shelvesTitle : copy('theme.footer_continued', { title: shelvesTitle })
              }
              className="flex flex-col items-start gap-3"
            >
              {column.map((category) => (
                <Link key={category.id} to={categoryPath(category.slug)} className="hover:text-white">
                  {category.name}
                </Link>
              ))}
            </nav>
          </LumeraColumn>
        ))}
        {preview ? null : (
          <LumeraColumn title={copy('theme.footer_support')}>
            <PageLinks className="mb-3 flex-col items-start justify-start gap-3" />
            <BlogLinks className="flex-col items-start justify-start gap-3" />
            <InstallAppSlot className="mt-4" />
            <AppStoreBadges footer={footer} preview={preview} className="mt-4" />
          </LumeraColumn>
        )}
      </StorefrontContainer>
      <div className="border-t" style={{ borderColor: `${CREAM}26` }}>
        <StorefrontContainer className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 py-5 text-[12px]">
          <LanguageSwitcherSlot placement="footer" preview={preview} />
          <span style={copyrightStyle(footer)}>
            © {new Date().getFullYear()} {storeName} ·{' '}
            {copy('theme.powered_by', { brand: 'NeonCarts' })}
          </span>
        </StorefrontContainer>
      </div>
    </footer>
  );
};
